<template>
  <transition name="fade">
    <div class="popup-mask" v-if="visible">
      <div class="popup-container">
        <div class="popup-close" @click="closePopup">×</div>

        <div class="popup-content">
          <div>
            <div class="popup-title">微信扫码登录</div>
            <div class="popup-subtitle">未注册的微信号将自动创建账号</div>
            <div class="qrcode-wrapper">
              <div class="qrcode-box">
                <!-- 加载中状态 -->
                <div v-if="!qrcode_url" class="qrcode-loading">
                  <div class="loading-spinner">
                    <div class="spinner-circle"></div>
                    <div class="spinner-circle"></div>
                    <div class="spinner-circle"></div>
                  </div>
                  <div class="loading-text">正在加载二维码...</div>
                </div>
                
                <!-- 二维码显示 -->
                <template v-else>
                  <img :src="qrcode_url" alt="微信二维码" class="popup-qrcode" />
                  <div class="weixin-box">
                    <img src="https://nvivo.obs.cn-east-3.myhuaweicloud.com/front/34.png" class="wexin" />微信扫码
                  </div>
                </template>
              </div>

              <div v-if="showGreenCheck" class="scan-success-overlay">
                <i class="el-icon-success"></i>
                <div class="check-message">扫描成功</div>
                <div class="check-message">请在公众号点击授权完成登录</div>
              </div>
            </div>
          </div>

          <div class="popup-agreement">
            注册登录即代表同意
            <a href="#/agreement?type=0" target="_blank" class="popup-link"
              >《{{ agreementPrefix }}在线用户协议》</a
            >
            <a href="#/agreement?type=1" target="_blank" class="popup-link"
              >《{{ agreementPrefix }}在线政策》</a
            >
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { generateQrcode, verifyLogin, checkTokenAPI } from '@/api/login'
// ✨【新增】导入 mapState 辅助函数
import { mapState } from 'vuex'

export default {
  name: 'WeChatLoginPopup',
  props: {
    // 父组件传入，用于控制弹窗显隐
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // --- 【代码修改开始】 ---
    // 中文注释：根据当前网站域名动态设置协议名称的前缀的逻辑已被移除。
    // agreementPrefix 已被移至下面的 'computed' (计算属性) 中，
    // 它将从 Vuex store (site.siteInfo.site_brand_name) 中动态获取。
    // --- 【代码修改结束】 ---

    return {
      // 协议前缀
      // agreementPrefix: agreementPrefix, // (已移除，移至 computed)
      // 二维码 URL
      qrcode_url: '',
      // 唯一标识二维码的 scene_str
      scene_str: '',
      // 轮询定时器
      pollingTimer: null,
      // 用户扫码但未授权时的提示蒙版显隐
      showGreenCheck: false
    }
  },
  // ✨【新增】计算属性
  computed: {
    // ... 映射 'site/siteInfo' state 到 this.siteInfo
    ...mapState('site', {
      siteInfo: 'siteInfo'
    }),

    /**
     * @description 动态计算协议前缀
     * @returns {string} 品牌名称 (例如 "NVivoAI", "atlas" 等)
     */
    agreementPrefix() {
      // 中文注释：从 store 中安全地获取当前站点的品牌名称
      // (与 GlobalOrderPayDialog.vue 中的逻辑相同)
      // 如果 siteInfo 尚未加载，提供一个默认值 'NVivoAI'。
      return (this.siteInfo && this.siteInfo.site_brand_name)
        ? this.siteInfo.site_brand_name
        : 'NVivoAI' // 默认前缀
    }
  },
  watch: {
    // 监听 visible，当为 true 时启动获取二维码和轮询，为 false 时清理
    visible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.openQRCodePopupLogic()
        } else {
          this.stopPolling()
          this.showGreenCheck = false
        }
      }
    }
  },
  methods: {
    // 当组件需要显示时，获取二维码并开始轮询
    async openQRCodePopupLogic() {
      try {
        // ✨【修改点 1】: 从 Vuex store 中获取 site_name
        // (您提供的 store 示例数据显示，接口返回的 site_name 字段为 "NVivoAi")
        const siteName = (this.siteInfo && this.siteInfo.site_name)
          ? this.siteInfo.site_name
          : null;

        if (!siteName) {
          console.warn('WeChatLoginPopup: 无法从 store 获取 siteInfo.site_name, 将使用 null 值调用 generateQrcode。');
        }

        // ✨【修改点 2】: 将 siteName 传递给 generateQrcode 接口
        // (这假设您的 @/api/login.js 中的 generateQrcode 函数
        // 会将此参数作为 query string (例如: /generate_qrcode?site_name=NVivoAi) 发送给后端)
        const res = await generateQrcode(siteName)

        if (res && res.code === 200) {
          this.qrcode_url = res.data.qrcode_url
          this.scene_str = res.data.scene_str
          this.startPolling()
        } else {
          console.error('获取二维码失败:', res.msg || '未知错误')
        }
      } catch (error) {
        console.error('获取二维码异常:', error)
      }
    },

    // 轮询扫码/授权状态
    startPolling() {
      this.stopPolling()
      this.pollingTimer = setInterval(async () => {
        try {
          const response = await verifyLogin(this.scene_str)
          const { code } = response
          if (code === 201) {
            // 未扫码
            this.showGreenCheck = false
          } else if (code === 202) {
            // 已扫码但未授权
            this.showGreenCheck = true
          } else if (code === 203 || code === 404) {
            console.error(response.data.msg || '二维码已失效')
          } else if (code === 200) {
            // 授权成功
            const { token, user } = response.data
            this.$store.dispatch('user/setToken', token)
            this.$store.dispatch('user/setUserInfo', user)
            this.$message.success('登录成功！')
            await checkTokenAPI(token)
            this.closePopup()
          }
        } catch (err) {
          console.error('轮询接口异常:', err)
        }
      }, 4000)
    },

    // 停止轮询
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    // 关闭弹窗
    closePopup() {
      // 向父组件触发事件，用于让父组件更新 visible=false
      this.$emit('close')
    }
  },
  beforeDestroy() {
    // 组件销毁前，清除轮询
    this.stopPolling()
  }
}
</script>

<style scoped lang="less">
/* (样式保持原样) */
.popup-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-container {
  position: relative;
  background-color: #fff;
  width: 479px;
  height: 497px;
  padding: 20px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.popup-close {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 20px;
  color: #aaa;
  cursor: pointer;
}

.popup-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.popup-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.popup-subtitle {
  font-size: 15px;
  color: #999;
  margin-top: 8px;
  text-align: center;
}

/* 二维码样式 */
.qrcode-wrapper {
  position: relative;
  margin-top: 60px;
}
.qrcode-box {
  width: 240px;
  height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 25px;
  box-shadow: 0px 1px 20px 3px rgba(59, 59, 59, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  .weixin-box {
    display: flex;
    align-items: center;
    .wexin {
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
  }
}
.popup-qrcode {
  width: 200px;
  height: 200px;
}

/* 加载动画样式 */
.qrcode-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.loading-spinner {
  display: flex;
  gap: 12px;
  align-items: center;
}

.spinner-circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  animation: bounce 1.4s infinite ease-in-out both;
}

.spinner-circle:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner-circle:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-text {
  font-size: 14px;
  color: #999;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 白色遮罩层 */
.scan-success-overlay {
  position: absolute;
  top: 14px;
  left: 20px;
  width: 200px;
  height: 200px;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  .el-icon-success {
    font-size: 40px;
    color: #67c23a;
  }
}

.check-icon {
  /* 圆圈中对号 */
  width: 60px;
  height: 60px;
  border: 2px solid #67c23a;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 10px;
  background-color: #67c23a;
}

.check-message {
  font-size: 14px;
  color: #333;
  text-align: center;
  font-weight: 500;
}

/* 协议文字 */
.popup-agreement {
  font-size: 13px;
  color: #999;
  line-height: 1.4;

  .popup-link {
    color: #999;
    margin-left: 6px;
    text-decoration: none;
  }
}

/* 动画过渡 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>